<template>
    <div class="iframe-template">
        <div
            v-if="!notFound"
            class="yleispalveluhaku-container"
        >
            <div class="header center">
                <h1 class="app-name-label">
                    {{ $t("App.Name.Yleispalveluhaku") }}
                </h1>
            </div>
            <div>
                <div class="search-element">
                    <search-address />
                </div>
                <div
                    v-show="tableData.data !== null"
                    class="result-table"
                >
                    <h1 class="center">
                        {{ placeName }}
                    </h1>
                    <dynamic-table :table-data="tableData" />
                </div>
            </div>
        </div>
        <div v-else>
            <not-found />
        </div>
    </div>
</template>

<script lang="ts">
    import L from "leaflet"
    import { Component, Vue, Watch } from "vue-facing-decorator"
    import SearchAddress from "@/components/SearchAddress.vue"
    import SearchResultModel from "@/models/searchresultmodel"
    import DynamicTable from "@/components/DynamicTable.vue"
    import ApiService from "../services/ApiService"
    import TableData from "@/models/tabledata"
    import PageWhitelistUtils from "@/helpers/pageWhitelistUtils"
    import NotFound from "@/views/NotFound.vue"
    import { store } from '@/store/store'

    @Component({
        components: {
            "search-address": SearchAddress,
            "dynamic-table": DynamicTable,
            "not-found": NotFound
        }
    })

    export default class YleispalveluhakuIframe extends Vue {
        apiService: ApiService = new ApiService(store.getters.baseHref)
        tableData = new TableData([], null, null)
        placeName = ""
        notFound: boolean = false
        apiUrl = "v1/Viestinta/LaajakaistaJaPuhelin/OikeusPuhelinJaLaajakaistaLiittymaanSijainninMukaan"

        // Alustetaan SearchAddress-komponentin tarvitsema EPSG:3067 projektio proj4-kirjastoon
        crs = new L.Proj.CRS("EPSG:3067",
                             "+proj=utm +zone=35 +ellps=GRS80 +units=m +no_defs", {
                                 origin: [-548576, 8388608],
                                 bounds: L.bounds([-548576, 8388608], [1548576, 6291456]),
                                 resolutions: [
                                     8192, 4096, 2048, 1024, 512, 256,
                                     128, 64, 32, 16, 8, 4, 2, 1, 0.5,
                                     0.25, 0.125, 0.0625, 0.03125, 0.015625
                                 ]
                             })

        async mounted(): Promise<void> {
            const pageWhitelistUtils = new PageWhitelistUtils()
            if (!pageWhitelistUtils.isSubjectOnWhiteList(this.$route.name?.toString() ?? "")) {
                // Sivu ei ole whitelistalla. Ei näytetä.
                this.notFound = true
            }
        }

        get searchAddressSelected(): SearchResultModel | null {
            return store.getters.searchAddressSelected
        }

        @Watch("searchAddressSelected")
        async onSearchAddressSelected(): Promise<void> {
            if (this.searchAddressSelected != null) {
                this.placeName = this.searchAddressSelected?.label ?? ""

                // Haetaan valitun paikan tiedot
                const filters = new URLSearchParams()
                filters.append("koordinaattiX", this.searchAddressSelected?.coordinates.lat !== undefined ? this.searchAddressSelected?.coordinates.lat.toString() : "")
                filters.append("koordinaattiY", this.searchAddressSelected?.coordinates.lng !== undefined ? this.searchAddressSelected?.coordinates.lng.toString() : "")
                filters.append("getGeoms", "false")
                const data = await this.apiService.dynamicApiCall(this.apiUrl, filters)
                const tableValues: Array<Array<string>> = []

                data[0].palvelulista.forEach(palvelu => {
                    tableValues.push([this.getLocalizedPalveluName(palvelu.yleispalvelu), palvelu.operaattorinimi])
                })

                this.tableData = new TableData(this.tableHeaders, tableValues)
            }
            else {
                this.placeName = ""
                this.tableData = new TableData([], null, null)
            }
        }

        getLocalizedPalveluName(yleispalvelu: string) : string {
            let name = yleispalvelu
            switch (yleispalvelu.toLocaleLowerCase()) {
                case "tekstiviestipalvelu":
                    name = this.$t("App.OikeusPuhelinJaLaajakaistaLiittymaan.DropDownList.Tekstiviestipalvelu") ?? "App.OikeusPuhelinJaLaajakaistaLiittymaan.DropDownList.Tekstiviestipalvelu"
                    break
                case "puhelinpalvelu":
                    name = this.$t("App.OikeusPuhelinJaLaajakaistaLiittymaan.DropDownList.Puhelinpalvelu") ?? "App.OikeusPuhelinJaLaajakaistaLiittymaan.DropDownList.Puhelinpalvelu"
                    break
                case "512k512klaajakaista":
                    name = this.$t("App.OikeusPuhelinJaLaajakaistaLiittymaan.DropDownList.Laajakaista") ?? "App.OikeusPuhelinJaLaajakaistaLiittymaan.DropDownList.Laajakaista"
                    break
                case "5mlaajakaista" :
                    name = this.$t("App.OikeusPuhelinJaLaajakaistaLiittymaan.DropDownList.5Mlaajakaista") ?? "App.OikeusPuhelinJaLaajakaistaLiittymaan.DropDownList.5Mlaajakaista"
                    break
            }

            return name
        }

        get tableHeaders(): string[] {
            const columns: string[] = []
            columns.push(this.$t("App.Yleispalveluhaku.TableHeader.Yleispalvelu") ?? "App.Yleispalveluhaku.TableHeader.Yleispalvelu")
            columns.push(this.$t("App.Yleispalveluhaku.TableHeader.Yleispalveluyritys") ?? "App.Yleispalveluhaku.TableHeader.Yleispalveluyritys")
            return columns
        }
    }
</script>

<style scoped>
    .iframe-template {
        width: 100%;
        height: 100%;
        padding: 0px 20px;
        background-color: #FFFFFF;
        display: flex;
        justify-content: center;
    }

    .yleispalveluhaku-container {
      max-width: 1100px;
      flex-grow: 1;
    }

    .center {
        width: 100%;
        display: flex;
        justify-content: center;
    }

    .search-element {
        background-color: #F2F4F8;
        padding: 10px 30px;
        margin-bottom: 20px;
    }

    /* Result table style modifications */
    .result-table :deep(h6) {
        width: 100%;
        display: flex;
        justify-content: center;
        margin-top: 5px;
    }

    .result-table :deep(.table-data-col) {
        text-align: center;
    }

    .result-table :deep(.search-group) {
        width: 100% !important;
    }

    .result-table :deep(td) {
        padding: 20px 10px;
    }
</style>
